// Instance
import MainAPI from "@/app/apis/main/main-api.js";

// Endpoints
import { API, ENDPOINTS } from "@/app/apis/main/_connection/connection-api.js";

const queryString = require("querystring")

/**
 * API class for managing Dam/Sire details.
 * Extends MainAPI to provide methods for interacting with the Dam/Sire endpoints.
 *
 * @class DamSireAPI
 * @extends MainAPI
 */
class DamSireAPI extends MainAPI {

    /**
     * Adds a new Dam/Sire by sending a POST request with form data.
     *
     * @async
     * @function addDamSire
     * @param {Object} damSireDetails - The details of the Dam/Sire to be added.
     * @param {Object} damSireDetails.formData - The form data for the Dam/Sire.
     * @param {File} [damSireDetails.dogImageUpload] - The file for the dog image (optional).
     * @returns {Promise<Object>} The response from the API.
     * @throws {Error} If the API request fails.
     */
    async addDamSire(damSireDetails) {
        let newFormData = new FormData();

        const { formData, dogImageUpload } = damSireDetails;

        newFormData.append("formData", JSON.stringify(formData));

        if (dogImageUpload) {
            newFormData.append("dogImageUpload", dogImageUpload);
        }

        return API.post(`${ENDPOINTS.POST_DAM_SIRE}`, newFormData, {
            header: {
                "Content-Type": "multipart/form-data",
            },
        });
    }

    /**
     * Updates an existing Dam/Sire by sending a PUT request with form data.
     *
     * @async
     * @function updateDamSire
     * @param {number} damSireId - The ID of the Dam/Sire to be updated.
     * @param {Object} damSireDetails - The updated details of the Dam/Sire.
     * @param {Object} damSireDetails.formData - The form data for the Dam/Sire.
     * @param {File} [damSireDetails.dogImageUpload] - The file for the dog image (optional).
     * @returns {Promise<Object>} The response from the API.
     * @throws {Error} If the API request fails.
     */
    async updateDamSire(damSireId, damSireDetails) {
        let newFormData = new FormData();

        const { formData, dogImageUpload } = damSireDetails;

        newFormData.append("formData", JSON.stringify(formData));

        if (dogImageUpload) {
            newFormData.append("dogImageUpload", dogImageUpload);
        }

        return API.put(`${ENDPOINTS.PUT_DAM_SIRE}/${damSireId}`, newFormData, {
            header: {
                "Content-Type": "multipart/form-data",
            },
        });
    }

    /**
     * Fetches the details of a Dam/Sire by sending a GET request.
     *
     * @async
     * @function getDamSireDetails
     * @param {number} damSireId - The ID of the Dam/Sire to retrieve.
     * @returns {Promise<Object>} The response from the API containing the Dam/Sire details.
     * @throws {Error} If the API request fails.
     */
    async getDamSireDetails(damSireId) {
        return API.get(`${ENDPOINTS.GET_DAM_SIRE}/${damSireId}`);
    }
    
    /**
     * Fetches the whelp types and previous litters for a given dam.
     *
     * @param {string} damId - The unique identifier of the dam.
     * @returns {Promise<Object>} A promise that resolves to the response data containing whelp types and previous litters.
     */
    async getDamWhelpTypesAndPrevLitters(damId) {
        return API.get(`${ENDPOINTS.GET_DAM_WHELP_TYPES_AND_PREV_LITTERS}/${damId}`);
    }

    // *********************************** DOCUMENTS ****************************************************
    /**
     * Adds a new document by sending a POST request with form data.
     *
     * @async
     * @function uploadDamSireDocument
     * @param {Object} uploadDetails - The details of the Dam/Sire to be added.
     * @param {Object} uploadDetails.formData - The form data for the Dam/Sire.
     * @param {File} [uploadDetails.document] - The file for the document (required).
     * @returns {Promise<Object>} The response from the API.
     * @throws {Error} If the API request fails.
    */
    async uploadDamSireDocument(uploadDetails) {
        let newFormData = new FormData();
        
        const { formData, document } = uploadDetails;
        
        newFormData.append("formData", JSON.stringify(formData));
        newFormData.append("document", document);
        
        return API.post(`${ENDPOINTS.POST_DAM_SIRE_DOCUMENT}`, newFormData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    }

    /**
     * Uploads multiple dam/sire documents to the server.
     *
     * This function constructs a `FormData` object, appends the form data and documents, 
     * and sends it via a `POST` request to the server for processing.
     *
     * @async
     * @function uploadMultipleDamSireDocuments
     * @param {Object} uploadDetails - The details of the documents to be uploaded.
     * @param {Object} uploadDetails.formData - The metadata information related to the upload.
     * @param {Array} uploadDetails.documents - The list of document files to be uploaded.
     * @returns {Promise<Object>} A promise that resolves to the server response after uploading the documents.
     * @throws {Error} Throws an error if the upload process fails.
    */
    async uploadMultipleDamSireDocuments(uploadDetails) {
        let newFormData = new FormData();
        
        const { formData, documents } = uploadDetails;
        
        newFormData.append("formData", JSON.stringify(formData));

        if(documents && documents.length > 0){
            documents.forEach(document => {
                newFormData.append("documents", document);
            })
        }
        
        return API.post(`${ENDPOINTS.POST_DAM_SIRE_DOCUMENTS_MULTIPLE}`, newFormData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    }
    
    /**
     * Fetches the list of Dam/Sire documents based on the provided query parameters.
     *
     * @async
     * @function getDamSireDocumentsList
     * @param {Object} queryParams - The query parameters used to filter or paginate the document list.
     * @param {string} [queryParams.damSireId] - The ID of the Dam/Sire to filter documents.
     * @param {string} [queryParams.sortBy] - The field to sort by (e.g., 'createdAt').
     * @param {string} [queryParams.filterBy] - The field to sort by (e.g., 'fileName', 'fileType').
     * @param {string} [queryParams.order] - The sort order ('asc' or 'desc').
     * @param {number} [queryParams.page] - The page number for pagination.
     * @param {number} [queryParams.limit] - The number of documents to retrieve per page.
     * @returns {Promise<Object>} The response from the API containing the list of documents.
     * @throws {Error} If the API request fails.
    */
    async getDamSireDocumentsList(queryParams) {
        return API.get(`${ENDPOINTS.GET_DAM_SIRE_DOCUMENTS_LIST}?${queryString.stringify(queryParams)}`);
    }

    /**
     * Fetches the list of dam/sire document progress based on the provided status.
     *
     * @async
     * @function getDamSireDocumentsListProgress
     * @param {number} damSireId - The ID of the dam/sire to fetch document progress for.
     * @param {string} status - The status of the documents to filter by (e.g., 'IN_PROGRESS', 'UPLOADED', 'ERROR').
     * @returns {Promise<Object>} A promise that resolves to the API response containing the list of document progress details.
     * @throws {Error} Throws an error if the API request fails.
     */
    async getDamSireDocumentsListProgress(damSireId, status) {
        return API.get(`${ENDPOINTS.GET_DAM_SIRE_DOCUMENTS_LIST_PROGRESS}/${damSireId}?status=${status}`);
    }

    /**
     * Deletes a dam/sire document from the server.
     * 
     * @async
     * @function deleteDamSireDocument
     * @param {string} documentId - The ID of the document to delete.
     * @param {Object} documentDetails - The details of the document being deleted.
     * @param {string} documentDetails.damSireId - The ID of the dam or sire associated with the document.
     * @returns {Promise<Object>} - The server response from the delete operation.
     * @throws {Error} If the API request fails.
     */
    async deleteDamSireDocument(documentId, documentDetails) {
        return API.delete(`${ENDPOINTS.DELETE_DAM_SIRE_DOCUMENT}/${documentId}`, { data: documentDetails });
    }

    /**
     * Updates a dam/sire document from the server.
     * 
     * @async
     * @function updateDamSireDocument
     * @param {string} documentId - The ID of the document to update.
     * @param {Object} documentDetails - The details of the document being updated.
     * @param {string} documentDetails.description - The descsription of the dam or sire associated with the document.
     * @returns {Promise<Object>} - The server response from the update operation.
     * @throws {Error} If the API request fails.
     */
    async updateDamSireDocument(documentId, documentDetails) {
        return API.put(`${ENDPOINTS.PUT_DAM_SIRE_DOCUMENT}/${documentId}`, documentDetails);
    }
}

export default new DamSireAPI();
